import { createContext, ReactNode, useContext } from "react";
import { CordataWork, CordataWorkTriple, ForrigeBokWork, HoldingsData } from "@libry-content/integrations";
import { Work } from "@biblioteksentralen/cordata";
import { useHoldingsData } from "../../hooks/useHoldingsData";
import { useForrigeBokWork } from "../../../components/forrigeBok/useForrigeBokData";
import { useAggregateWorkTriples } from "../../cordata/useAggregateWorkTriples";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { getCurrentVariantFromState } from "./getCurrentVariantFromState";
import {
  ManifestationStatusForUserType,
  useManifestationStatusForUser,
} from "../manifestations/useManifestationStatusForUser";
import { getHoldingCountForWorkTriple } from "../../hooks/useHoldingCounts";
import { SetVariantState, useCurrentVariantUrlState, VariantState } from "./useCurrentVariantUrlState";
import { LibraryInfo } from "../../sanityQueries";
import { WorkPageRecommendation } from "../../../app/[locale]/sites/[siteDomain]/samling/verk/[workId]/fetchRecommendationData";
import { KulturfondVideo } from "@libry-content/types";
import { CordataVariant } from "@libry-content/integrations/src/bmdb/CordataVariant";

export interface WorkViewContextInterface {
  work?: CordataWork;
  currentVariantState?: VariantState;
  setVariantState: SetVariantState;
  holdingsData?: HoldingsData;
  hasValidHoldings?: boolean;
  forrigeBokWork?: ForrigeBokWork;
  worksAssociatedWithAggregateManifestations?: Record<string, Work[]> | undefined;
  currentVariant?: { variant: CordataVariant; workTriples: CordataWorkTriple[] };
  currentRepresentativeWorkTriple?: CordataWorkTriple;
  manifestationStatusForUser?: ManifestationStatusForUserType;
  libraries?: LibraryInfo[];
  recommendation?: WorkPageRecommendation;
  kulturFondVideo?: KulturfondVideo;
}

export const WorkViewContext = createContext<WorkViewContextInterface>({
  setVariantState: () => {},
});

export const useWorkViewContext = () => useContext(WorkViewContext);

type WorkViewContextProviderProps = {
  children: ReactNode;
  work: Work;
  libraries: LibraryInfo[];
  recommendation?: WorkPageRecommendation;
  kulturFondVideo?: KulturfondVideo;
};

export const WorkViewContextProvider = ({
  children,
  work,
  libraries,
  recommendation,
  kulturFondVideo,
}: WorkViewContextProviderProps) => {
  const { currentVariantState, setVariantState } = useCurrentVariantUrlState();
  const { site } = useCommonData();

  const forrigeBokWork = useForrigeBokWork({ workId: work?.id });

  const holdingsData = useHoldingsData(new CordataWork(work));
  const workFilteredOnHoldings = new CordataWork(work).filterOnHoldings(holdingsData.holdings);
  // Siden verket har fått filtrert vekk manifestasjoner som ikke har gyldig beholdning, kan vi sjekke om det er noen manifestasjoner igjen for å bestemme om verket har noe beholdning
  const hasValidHoldings = workFilteredOnHoldings.getWorkTriples().length > 0;
  const currentVariant = getCurrentVariantFromState({ work: workFilteredOnHoldings, currentVariantState });
  const currentRepresentativeWorkTriple =
    currentVariant?.workTriples.find((workTriple) => {
      const holdingCount = getHoldingCountForWorkTriple(workTriple, holdingsData.holdings, site);
      if (!holdingCount) return undefined;
      if (holdingCount.availableForLoan > 0) return true;
    }) ?? currentVariant?.workTriples[0];

  const worksAssociatedWithAggregateManifestations = useAggregateWorkTriples(workFilteredOnHoldings);

  const manifestationStatusForUser = useManifestationStatusForUser(
    currentVariant?.workTriples.map((workTriple) => workTriple.getRawBmdbManifestation()) ?? []
  );

  return (
    <WorkViewContext.Provider
      value={{
        currentVariantState,
        setVariantState,
        currentVariant,
        currentRepresentativeWorkTriple,
        work: workFilteredOnHoldings,
        holdingsData,
        hasValidHoldings,
        worksAssociatedWithAggregateManifestations,
        manifestationStatusForUser,
        forrigeBokWork,
        libraries,
        recommendation,
        kulturFondVideo,
      }}
    >
      {children}
    </WorkViewContext.Provider>
  );
};
